import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Avatar,
  Menu,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  Code as DeveloperIcon,
  Visibility as ViewerIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api, UserPermissions, TabPermissions } from '../services/api-client';
import NotificationSnackbar from '../components/NotificationSnackbar';

type UserRole = 'admin' | 'developer' | 'viewer';

// Tab display names and available actions
const TAB_CONFIG: Record<keyof UserPermissions, { label: string; actions: (keyof TabPermissions)[] }> = {
  dashboard: { label: 'Dashboard', actions: ['view'] },
  services: { label: 'Services', actions: ['view', 'edit'] },
  permissions: { label: 'Permissions', actions: ['view', 'edit'] },
  service_discovery: { label: 'Service Discovery', actions: ['view', 'edit', 'sync'] },
  settings: { label: 'Settings', actions: ['view', 'edit'] },
  audit_log: { label: 'Audit Log', actions: ['view'] },
  generic_terms: { label: 'Generic Terms', actions: ['view', 'edit'] },
  onboarding_survey: { label: 'Onboarding Survey', actions: ['view', 'edit', 'upload'] },
  user_management: { label: 'User Management', actions: ['view', 'edit'] },
  content: { label: 'Content', actions: ['view', 'edit', 'publish'] },
  feature_flags: { label: 'Feature Flags', actions: ['view', 'edit'] },
  staff: { label: 'Staff', actions: ['view', 'edit'] },
};

interface CommandCenterUser {
  email: string;
  role: UserRole;
  addedBy?: string;
  created_at?: string;
  createdAt?: string | Date;
  last_login?: string;
  lastLogin?: string;
  updated_at?: string;
  lastModified?: string | Date;
  updated_by?: string;
  modifiedBy?: string;
  name?: string;
  domain?: string;
  effective_permissions?: UserPermissions;
  has_custom_permissions?: boolean;
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CommandCenterUser | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CommandCenterUser | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Permissions editing state
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<Partial<UserPermissions> | null>(null);
  const [permissionsUser, setPermissionsUser] = useState<CommandCenterUser | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    role: 'viewer' as UserRole,
    message: ''
  });

  // Fetch users
  const { data: users, isLoading, error } = useQuery(
    'commandCenterUsers',
    async () => {
      const users = await api.getUsers();
      return users as CommandCenterUser[];
    }
  );

  // Add user mutation (invitation)
  const addUserMutation = useMutation(
    async (userData: { email: string; role: UserRole; message?: string }) => {
      // Send invitation email
      const response = await api.inviteUser(userData.email, userData.role, userData.message);
      return response;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('commandCenterUsers');
        setAddDialogOpen(false);
        setFormData({ email: '', role: 'viewer', message: '' });
        setSnackbar({
          open: true,
          message: 'Invitation sent successfully. The user will receive an email with instructions.',
          severity: 'success'
        });
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: `Failed to add user: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  // Update user role mutation
  const updateUserMutation = useMutation(
    async ({ email, role }: { email: string; role: UserRole }) => {
      await api.updateUserRole(email, role);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('commandCenterUsers');
        setEditingUser(null);
        setSnackbar({
          open: true,
          message: 'User role updated successfully',
          severity: 'success'
        });
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: `Failed to update user: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    async (email: string) => {
      await api.deleteUser(email);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('commandCenterUsers');
        setDeleteConfirmOpen(false);
        setSelectedUser(null);
        setSnackbar({
          open: true,
          message: 'User removed successfully',
          severity: 'success'
        });
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: `Failed to remove user: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  // Update user permissions mutation
  const updatePermissionsMutation = useMutation(
    async ({ email, permissions }: { email: string; permissions: Partial<UserPermissions> | null }) => {
      return await api.updateUserPermissions(email, permissions);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('commandCenterUsers');
        setPermissionsDialogOpen(false);
        setPermissionsUser(null);
        setEditingPermissions(null);
        setSnackbar({
          open: true,
          message: 'User permissions updated successfully',
          severity: 'success'
        });
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: `Failed to update permissions: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  // Open permissions dialog
  const handleOpenPermissions = (user: CommandCenterUser) => {
    setPermissionsUser(user);
    // Start with existing custom permissions or empty object
    setEditingPermissions(user.effective_permissions ? { ...user.effective_permissions } : {});
    setPermissionsDialogOpen(true);
    handleMenuClose();
  };

  // Toggle a specific permission
  const handleTogglePermission = (tab: keyof UserPermissions, action: keyof TabPermissions) => {
    if (!editingPermissions) return;

    setEditingPermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[tab]) {
        newPerms[tab] = {};
      }
      newPerms[tab] = {
        ...newPerms[tab],
        [action]: !(newPerms[tab]?.[action] ?? false)
      };
      return newPerms;
    });
  };

  // Reset permissions to role defaults
  const handleResetPermissions = () => {
    if (permissionsUser) {
      updatePermissionsMutation.mutate({ email: permissionsUser.email, permissions: null });
    }
  };

  // Save custom permissions
  const handleSavePermissions = () => {
    if (permissionsUser && editingPermissions) {
      updatePermissionsMutation.mutate({
        email: permissionsUser.email,
        permissions: editingPermissions
      });
    }
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'developer':
        return <DeveloperIcon fontSize="small" />;
      case 'viewer':
        return <ViewerIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'developer':
        return 'primary';
      case 'viewer':
        return 'default';
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: CommandCenterUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddUser = () => {
    if (!formData.email) return;

    // Normalize email to lowercase to prevent case-sensitivity issues
    const normalizedEmail = formData.email.toLowerCase().trim();

    // Validate email domain
    const validDomains = ['@tripsby.ai', '@byai.group'];
    const isValidDomain = validDomains.some(domain => normalizedEmail.endsWith(domain));

    if (!isValidDomain) {
      setSnackbar({
        open: true,
        message: 'Email must be from @tripsby.ai or @byai.group domain',
        severity: 'error'
      });
      return;
    }

    addUserMutation.mutate({ ...formData, email: normalizedEmail });
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Only administrators can manage users.
        </Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>User Management</Typography>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>User Management</Typography>
        <Alert severity="error">Failed to load users</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">User Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage access to the BY.AI Group Command Center
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Invite User
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Only users with @tripsby.ai or @byai.group email addresses can be added to the Command Center.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Added On</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user.email} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {user.email[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{user.email}</Typography>
                      {user.email === currentUser?.email && (
                        <Typography variant="caption" color="text.secondary">
                          (You)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getRoleIcon(user.role)}
                    label={user.role}
                    size="small"
                    color={getRoleColor(user.role)}
                  />
                </TableCell>
                <TableCell>
                  {user.role === 'admin' ? (
                    <Tooltip title="Admins have unrestricted access to all features">
                      <Chip
                        label="Full Access"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : user.has_custom_permissions ? (
                    <Tooltip title="This user has custom permissions. Click Edit Permissions to view details.">
                      <Chip
                        icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                        label="Custom"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title={user.role === 'developer' ? 'Default developer permissions' : 'Default viewer permissions'}>
                      <Chip
                        label={user.role === 'developer' ? 'Developer Default' : 'Viewer Default'}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {user.created_at || user.createdAt
                      ? format(new Date(user.created_at || user.createdAt as string), 'MMM d, yyyy')
                      : 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {user.updated_at || user.lastModified ? (
                    <Box>
                      <Typography variant="body2">
                        {format(new Date(user.updated_at || user.lastModified as string), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {user.updated_by || user.modifiedBy || 'Unknown'}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Never
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, user)}
                    disabled={user.email === currentUser?.email}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New User</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will send an invitation email to the user. They will need to sign in with their Google account to access the Command Center.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              helperText="Must be @tripsby.ai or @byai.group email"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <MenuItem value="viewer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ViewerIcon fontSize="small" />
                    Viewer (Read-only access)
                  </Box>
                </MenuItem>
                <MenuItem value="developer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeveloperIcon fontSize="small" />
                    Developer (Can modify permissions)
                  </Box>
                </MenuItem>
                <MenuItem value="admin">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AdminIcon fontSize="small" />
                    Admin (Full access)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Personal Message (Optional)"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Add a personal message to include in the invitation email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={!formData.email}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingUser} onClose={() => setEditingUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Changing role for: <strong>{editingUser?.email}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Role</InputLabel>
              <Select
                value={editingUser?.role || 'viewer'}
                label="New Role"
                onChange={(e) => {
                  if (editingUser) {
                    updateUserMutation.mutate({
                      email: editingUser.email,
                      role: e.target.value as UserRole
                    });
                  }
                }}
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="developer">Developer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{selectedUser?.email}</strong> from the Command Center?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The user will lose all access immediately.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.email)}
          >
            Remove User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setEditingUser(selectedUser);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Change Role
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleOpenPermissions(selectedUser)}
          disabled={selectedUser?.role === 'admin'}
        >
          <SecurityIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Permissions
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setDeleteConfirmOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Remove User
        </MenuItem>
      </Menu>

      {/* Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            Edit Permissions: {permissionsUser?.email}
          </Box>
        </DialogTitle>
        <DialogContent>
          {permissionsUser?.role === 'admin' ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Admin users automatically have full access to all features. Custom permissions cannot be set for admins.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                Custom permissions override the default permissions for the user's role ({permissionsUser?.role}).
                Toggle specific permissions below or reset to role defaults.
              </Alert>

              {permissionsUser?.has_custom_permissions && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This user has custom permissions. Click "Reset to Defaults" to restore role-based defaults.
                </Alert>
              )}

              {Object.entries(TAB_CONFIG).map(([tabKey, config]) => (
                <Accordion key={tabKey} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography sx={{ fontWeight: 500 }}>{config.label}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', mr: 2 }}>
                        {config.actions.map(action => {
                          const isEnabled = editingPermissions?.[tabKey as keyof UserPermissions]?.[action] ?? false;
                          return (
                            <Chip
                              key={action}
                              label={action}
                              size="small"
                              color={isEnabled ? 'success' : 'default'}
                              variant={isEnabled ? 'filled' : 'outlined'}
                              sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {config.actions.map(action => (
                        <FormControlLabel
                          key={action}
                          control={
                            <Switch
                              checked={editingPermissions?.[tabKey as keyof UserPermissions]?.[action] ?? false}
                              onChange={() => handleTogglePermission(tabKey as keyof UserPermissions, action)}
                              color="primary"
                            />
                          }
                          label={
                            <Typography sx={{ textTransform: 'capitalize' }}>
                              {action === 'view' ? 'Can View' :
                               action === 'edit' ? 'Can Edit' :
                               action === 'upload' ? 'Can Upload' :
                               action === 'sync' ? 'Can Sync' : action}
                            </Typography>
                          }
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            startIcon={<ResetIcon />}
            onClick={handleResetPermissions}
            disabled={!permissionsUser?.has_custom_permissions || updatePermissionsMutation.isLoading}
            color="warning"
          >
            Reset to Defaults
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setPermissionsDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePermissions}
            disabled={updatePermissionsMutation.isLoading || permissionsUser?.role === 'admin'}
            startIcon={updatePermissionsMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
}
